"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

// ============================================
// TYPES
// ============================================
export type ToggleVariant = "default" | "outline";
export type ToggleSize = "sm" | "default" | "lg";

// ============================================
// TOGGLE COMPONENT
// ============================================
export interface ToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant */
  variant?: ToggleVariant;
  /** Size preset */
  size?: ToggleSize;
  /** Controlled pressed state */
  pressed?: boolean;
  /** Uncontrolled default pressed state */
  defaultPressed?: boolean;
  /** Called when pressed state changes */
  onPressedChange?: (pressed: boolean) => void;
}

const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      className,
      variant = "default",
      size = "default",
      pressed,
      defaultPressed = false,
      onPressedChange,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const isControlled = pressed !== undefined;
    const [internalPressed, setInternalPressed] = React.useState(defaultPressed);
    const isPressed = isControlled ? pressed : internalPressed;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      const newPressed = !isPressed;
      if (!isControlled) {
        setInternalPressed(newPressed);
      }
      onPressedChange?.(newPressed);
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        type="button"
        data-slot="toggle"
        data-state={isPressed ? "on" : "off"}
        aria-pressed={isPressed}
        className={cn(
          styleDrivers.toggle.root,
          styleDrivers.toggle.variants[variant],
          styleDrivers.toggle.sizes[size],
          className
        )}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Toggle.displayName = "Toggle";

// ============================================
// TOGGLE GROUP COMPONENT
// ============================================
export type ToggleGroupType = "single" | "multiple";

export interface ToggleGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "defaultValue"> {
  /** Selection type */
  type?: ToggleGroupType;
  /** Controlled value (string for single, string[] for multiple) */
  value?: string | string[];
  /** Uncontrolled default value */
  defaultValue?: string | string[];
  /** Called when value changes */
  onValueChange?: (value: string | string[]) => void;
  /** Visual variant for all items */
  variant?: ToggleVariant;
  /** Size preset for all items */
  size?: ToggleSize;
  /** Orientation */
  orientation?: "horizontal" | "vertical";
  /** Disabled state */
  disabled?: boolean;
}

interface ToggleGroupContextValue {
  type: ToggleGroupType;
  value: string[];
  onItemClick: (itemValue: string) => void;
  variant: ToggleVariant;
  size: ToggleSize;
  disabled: boolean;
}

const ToggleGroupContext = React.createContext<ToggleGroupContextValue | null>(null);

const useToggleGroup = () => {
  const context = React.useContext(ToggleGroupContext);
  if (!context) {
    throw new Error("ToggleGroupItem must be used within a ToggleGroup");
  }
  return context;
};

const ToggleGroup = React.forwardRef<HTMLDivElement, ToggleGroupProps>(
  (
    {
      className,
      type = "single",
      value,
      defaultValue,
      onValueChange,
      variant = "default",
      size = "default",
      orientation = "horizontal",
      disabled = false,
      children,
      ...props
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState<string[]>(() => {
      if (defaultValue) {
        return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
      }
      return [];
    });

    const currentValue = isControlled
      ? Array.isArray(value)
        ? value
        : value
          ? [value]
          : []
      : internalValue;

    const handleItemClick = (itemValue: string) => {
      if (disabled) return;

      let newValue: string[];

      if (type === "single") {
        newValue = currentValue.includes(itemValue) ? [] : [itemValue];
      } else {
        newValue = currentValue.includes(itemValue)
          ? currentValue.filter((v) => v !== itemValue)
          : [...currentValue, itemValue];
      }

      if (!isControlled) {
        setInternalValue(newValue);
      }

      onValueChange?.(type === "single" ? (newValue[0] ?? "") : newValue);
    };

    return (
      <div
        ref={ref}
        role="group"
        data-slot="toggle-group"
        data-orientation={orientation}
        className={cn(
          styleDrivers.toggleGroup.root,
          orientation === "vertical" && "flex-col",
          className
        )}
        {...props}
      >
        <ToggleGroupContext.Provider
          value={{
            type,
            value: currentValue,
            onItemClick: handleItemClick,
            variant,
            size,
            disabled,
          }}
        >
          {children}
        </ToggleGroupContext.Provider>
      </div>
    );
  }
);

ToggleGroup.displayName = "ToggleGroup";

// ============================================
// TOGGLE GROUP ITEM COMPONENT
// ============================================
export interface ToggleGroupItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Value for this item */
  value: string;
  /** Override group variant */
  variant?: ToggleVariant;
  /** Override group size */
  size?: ToggleSize;
}

const ToggleGroupItem = React.forwardRef<HTMLButtonElement, ToggleGroupItemProps>(
  ({ className, value, variant, size, disabled, children, ...props }, ref) => {
    const context = useToggleGroup();
    const finalVariant = variant ?? context.variant;
    const finalSize = size ?? context.size;
    const isPressed = context.value.includes(value);
    const isDisabled = disabled ?? context.disabled;

    const sizeClasses = {
      sm: "h-7 px-2 text-caption",
      default: "h-8 px-3 text-body",
      lg: "h-9 px-4 text-body",
    };

    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        data-slot="toggle-group-item"
        data-state={isPressed ? "on" : "off"}
        aria-pressed={isPressed}
        aria-checked={isPressed}
        disabled={isDisabled}
        className={cn(
          styleDrivers.toggleGroup.item,
          sizeClasses[finalSize],
          finalVariant === "outline" && "border border-border",
          className
        )}
        onClick={() => context.onItemClick(value)}
        {...props}
      >
        {children}
      </button>
    );
  }
);

ToggleGroupItem.displayName = "ToggleGroupItem";

// ============================================
// EXPORTS
// ============================================
export { Toggle, ToggleGroup, ToggleGroupItem, useToggleGroup };
